import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import MobileScanInput from '@/modules/mobile/components/MobileScanInput';
import {
  Truck,
  Package,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  MapPin,
  Clock,
  ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import { useDriver } from '../context/DriverContext';
import { parcelEvents } from '@/shared/services/trailService';

const API_URL = API_BASE;

const STEPS = {
  SELECT_ROUTE: 'select_route',
  LOAD_PACKAGES: 'load_packages',
  CONFIRM: 'confirm'
};

const RouteLoad = () => {
  const navigate = useNavigate();
  const { startRoute, addLoadedPackage, loadedPackages, setActiveRoute } = useDriver();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_ROUTE);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [packages, setPackages] = useState([]);
  
  // Dynamic state for available routes
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch available routes from API
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await fetch(`${API_URL}/api/delivery/driver/available-routes`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setAvailableRoutes(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch routes:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRoutes();
  }, []);

  // Generate packages when route selected
  useEffect(() => {
    if (selectedRoute) {
      const pkgs = Array.from({ length: selectedRoute.packages }, (_, i) => ({
        id: `PKG-${selectedRoute.id}-${String(i + 1).padStart(3, '0')}`,
        orderId: `ORD-${String(1000 + i)}`,
        stopNumber: (i % selectedRoute.stops) + 1,
        customer: `Customer ${i + 1}`,
        address: `${100 + i} Main Street`,
        weight: (Math.random() * 10 + 1).toFixed(1),
        loaded: false
      }));
      setPackages(pkgs);
    }
  }, [selectedRoute]);

  const loadedCount = packages.filter(p => p.loaded).length;
  const progress = packages.length > 0 ? (loadedCount / packages.length) * 100 : 0;

  const handleSelectRoute = (route) => {
    setSelectedRoute(route);
    setCurrentStep(STEPS.LOAD_PACKAGES);
  };

  const handlePackageScan = (barcode) => {
    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    
    const pkgIndex = packages.findIndex(p => 
      p.id === barcode || barcode.includes(p.id) || barcode.includes(p.orderId)
    );

    if (pkgIndex >= 0) {
      const pkg = packages[pkgIndex];
      
      if (pkg.loaded) {
        toast.warning('Already Loaded', { description: `${pkg.id} is already on the truck` });
        return;
      }

      setPackages(prev => prev.map((p, idx) => 
        idx === pkgIndex ? { ...p, loaded: true } : p
      ));
      addLoadedPackage(pkg);
      
      // Emit trail event for package scanned
      parcelEvents.routePackageScanned(selectedRoute.id, pkg.id, driverId);

      if (loadedCount + 1 === packages.length) {
        toast.success('All Packages Loaded!', { description: 'Ready to start route' });
      } else {
        toast.success('Package Loaded', { description: `${packages.length - loadedCount - 1} remaining` });
      }
    } else {
      toast.error('Wrong Route', { description: 'This package is not assigned to this route' });
    }
  };

  const handleStartRoute = () => {
    // Create stops from packages
    const stops = [];
    const stopMap = {};
    
    packages.forEach(pkg => {
      if (!stopMap[pkg.stopNumber]) {
        stopMap[pkg.stopNumber] = {
          id: `STOP-${selectedRoute.id}-${pkg.stopNumber}`,
          number: pkg.stopNumber,
          customer: pkg.customer,
          address: pkg.address,
          packages: [],
          status: 'PENDING'
        };
      }
      stopMap[pkg.stopNumber].packages.push(pkg);
    });
    
    Object.values(stopMap).sort((a, b) => a.number - b.number).forEach(stop => {
      stops.push(stop);
    });

    const routeWithStops = {
      ...selectedRoute,
      stops,
      loadedPackages: packages.filter(p => p.loaded)
    };

    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    const packageIds = packages.filter(p => p.loaded).map(p => p.id);
    
    // Emit trail events for route loaded and started
    parcelEvents.routeLoaded(selectedRoute.id, packageIds, driverId);
    parcelEvents.routeStarted(selectedRoute.id, driverId);

    startRoute(routeWithStops);
    setActiveRoute(routeWithStops);
    navigate('/driver/route-active');
    toast.success('Route Started', { description: `${selectedRoute.id} is now active` });
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SELECT_ROUTE:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Select Route to Load</h2>
            
            {availableRoutes.map((route) => (
              <Card 
                key={route.id}
                className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all"
                onClick={() => handleSelectRoute(route)}
                data-testid={`select-route-${route.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-black text-white">
                      <Truck className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{route.id}</h3>
                        {route.allowResequence && (
                          <Badge variant="outline" className="text-xs">Flexible</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{route.vehicle}</p>
                      <div className="flex items-center gap-3 mt-1 text-sm">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {route.stops} stops
                        </span>
                        <span className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {route.packages} pkg
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge>{route.departure}</Badge>
                      <p className="text-xs text-muted-foreground mt-1">{route.zone}</p>
                      <ChevronRight className="h-5 w-5 text-muted-foreground mt-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case STEPS.LOAD_PACKAGES:
        return (
          <div className="space-y-4">
            {/* Progress Header */}
            <Card className="bg-gradient-to-r from-black to-gray-800 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm opacity-80">Loading {selectedRoute.id}</p>
                    <p className="text-2xl font-bold">{loadedCount} / {packages.length}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-80">Vehicle</p>
                    <p className="text-sm font-medium">{selectedRoute.vehicle}</p>
                  </div>
                </div>
                <Progress value={progress} className="h-2 bg-white/20" />
              </CardContent>
            </Card>

            {/* Scanner */}
            <MobileScanInput
              title="Scan Package"
              subtitle="Scan each package before loading"
              gateId="GATE_LOAD_PKG"
              barcodeType="CARTON"
              scanContext="LOADING"
              onScanSuccess={handlePackageScan}
              validateScan={async (barcode) => {
                const pkg = packages.find(p => 
                  p.id === barcode || barcode.includes(p.id) || barcode.includes(p.orderId)
                );
                if (!pkg) return { success: false, result: 'REJECTED', message: 'Not on this route' };
                if (pkg.loaded) return { success: false, result: 'WARNING', message: 'Already loaded' };
                return { success: true, result: 'OK' };
              }}
            />

            {/* Package List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Packages
                  </span>
                  <Badge variant={loadedCount === packages.length ? 'default' : 'outline'}>
                    {loadedCount}/{packages.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-48 overflow-y-auto">
                {packages.map((pkg) => (
                  <div key={pkg.id} className="px-4 py-2 border-b last:border-0 flex items-center gap-3">
                    {pkg.loaded ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <ScanLine className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono truncate">{pkg.id}</p>
                      <p className="text-xs text-muted-foreground">Stop {pkg.stopNumber} • {pkg.weight}kg</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setCurrentStep(STEPS.SELECT_ROUTE);
                  setSelectedRoute(null);
                  setPackages([]);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              {loadedCount > 0 && (
                <Button 
                  onClick={handleStartRoute}
                  className={`flex-1 ${loadedCount === packages.length ? 'bg-green-500 hover:bg-green-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                  data-testid="start-route-btn"
                >
                  {loadedCount === packages.length ? 'Start Route' : `Start (${loadedCount}/${packages.length})`}
                </Button>
              )}
            </div>

            {loadedCount < packages.length && loadedCount > 0 && (
              <p className="text-xs text-center text-amber-600 flex items-center justify-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Some packages not scanned
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-4" data-testid="route-load">
      {renderStep()}
    </div>
  );
};

export default RouteLoad;
